import {
  IconAlertCircle,
  IconBarcode,
  IconBox,
  IconCategory,
  IconDatabase,
  IconFileText,
  IconPackage,
  IconPencil,
  IconPhoto,
  IconRuler,
  IconScale,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { mapServerItem } from "./ItemEditor/itemMapping";
import {
  calculateSteelWeight,
  formatLength,
  formatUnitWeight,
  formatWeight,
  matchesDerivedUnitWeight,
  roundUnitWeight,
  steelWeight,
} from "./ItemEditor/steelWeight";
import { type Item } from "./ItemEditor/data";

function steelUnitWeight(item: Item | null) {
  if (!item || item.categoryId !== "steel" || !item.steelType || !item.spec) {
    return null;
  }
  const result = calculateSteelWeight(item.steelType, item.spec);
  return result ? result.unitWeightKgM : null;
}

function ProductListPage() {
  const { t } = useTranslation();
  const [productData, setProductData] = useState({
    name: "",
    description: "",
    inventoryLowerLimitContactId: null,
    item: null,
    defaultItem: null,
    unitWeight: null,
    unit: null,
    material: null,
    partnerId: null,
    inventoryLowerLimit: null,
    length: "",
    image: "",
    productInventory: [],
  });
  const [partnerData, setPartnerData] = useState<
    { id: string; name: string }[]
  >([]);
  const [employeeData, setEmployeeData] = useState<
    { id: string; name: string }[]
  >([]);
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    name,
    description,
    inventoryLowerLimitContactId,
    item,
    defaultItem,
    unitWeight,
    unit,
    material,
    partnerId,
    inventoryLowerLimit,
    length,
    image,
  } = productData;

  const itemSource = item || defaultItem;
  const itemSourceType = item
    ? t("warehouse.companyItem") || "公司品項"
    : defaultItem
      ? t("warehouse.defaultItem") || "預設品項"
      : "";

  const mappedItem = itemSource
    ? mapServerItem(itemSource as any, !!defaultItem)
    : null;
  const categoryId = mappedItem?.categoryId ?? null;
  const isSteel = categoryId === "steel";
  const isScrew = categoryId === "screw";
  const isOther = !!itemSource && !isSteel && !isScrew;

  const derivedUnitWeight = roundUnitWeight(steelUnitWeight(mappedItem));
  const displayUnitWeight =
    derivedUnitWeight != null &&
    matchesDerivedUnitWeight(derivedUnitWeight, unitWeight)
      ? derivedUnitWeight
      : unitWeight;

  const weight = isSteel ? steelWeight(displayUnitWeight, length) : null;

  const partnerName = partnerData.find((p) => p?.id === partnerId)?.name;
  const contactName = employeeData.find(
    (e) => e?.id === inventoryLowerLimitContactId,
  )?.name;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await single(orpcClient.product.read([{ id: id! }]));
        if (!result) return;

        setProductData(result);
      } catch (error) {
        console.error("Error fetching product data:", error);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const result = await orpcClient.partner.list({});
        setPartnerData(result);
      } catch (error) {
        console.error("Error fetching partner data:", error);
      }
    };

    fetchPartners();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchEmployees();
  }, []);

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>,
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
        {value || <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {name || t("warehouse.productManagement") || "商品資訊"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {description || t("warehouse.productDescription") || "商品資訊"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/hrMainPage/editProduct/${id}`)}
            className="cursor-pointer flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
          >
            <IconPencil size={18} />
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(
              t("warehouse.productName") || "商品名稱",
              name,
              IconPackage,
            )}
            {/* {renderField(
              t("warehouse.productCode") || "商品編號",
              code,
              IconBarcode,
            )} */}
            {renderField(
              t("warehouse.selectItem") || "品項",
              itemSource?.name ?? "",
              IconBox,
            )}
            {renderField(
              t("warehouse.itemSource") || "品項來源",
              itemSourceType,
              IconDatabase,
            )}
            {isSteel &&
              renderField(
                t("warehouse.unitWeight") || "單位重(kg/m)",
                formatUnitWeight(displayUnitWeight),
                IconScale,
              )}
            {(isSteel || isScrew) &&
              renderField(
                t("warehouse.length") || "長度",
                formatLength(length),
                IconRuler,
              )}
            {isSteel &&
              renderField(
                t("warehouse.weight") || "重量",
                formatWeight(weight),
                IconScale,
              )}
            {(isScrew || isOther) &&
              renderField(t("warehouse.unit") || "單位", unit, IconScale)}
            {renderField(t("warehouse.material") || "材質", material, IconCategory)}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconUsers size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.partner") || "合作方"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            {renderField(
              t("warehouse.partner") || "合作方",
              partnerName,
              IconUsers,
            )}
            {renderField(
              t("warehouse.productDescription") || "商品描述",
              description,
              IconFileText,
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPhoto size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.productImage") || "商品圖片"}
            </h3>
          </div>
          <div className="p-6">
            {image ? (
              <div className="h-40 w-full max-w-[420px] overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                <img
                  src={image}
                  alt={name}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400">
                —
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconAlertCircle size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.inventoryLowerLimit") || "設定庫存下限"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            {renderField(
              t("warehouse.inventoryLowerLimit") || "設定庫存下限",
              inventoryLowerLimit,
              IconAlertCircle,
            )}
            {renderField(
              t("warehouse.inventoryLowerLimitContact") || "庫存下限通知人",
              contactName,
              IconUser,
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default ProductListPage;
